import React, { useState, useContext } from "react";

import MenuItem from "@mui/material/MenuItem";
import ListItemIcon from "@mui/material/ListItemIcon";
import ListItemText from "@mui/material/ListItemText";
import Box from "@mui/material/Box";
import Divider from "@mui/material/Divider";
import ReplyIcon from "@mui/icons-material/Reply";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import AutoAwesomeIcon from "@mui/icons-material/AutoAwesome";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import StarBorderIcon from "@mui/icons-material/StarBorder";
import StarIcon from "@mui/icons-material/Star";
import CheckBoxOutlinedIcon from "@mui/icons-material/CheckBoxOutlined";
import ForwardIcon from "@mui/icons-material/Forward";
import AddReactionIcon from "@mui/icons-material/AddReaction";

import { i18n } from "../../translate/i18n";
import api from "../../services/api";
import ConfirmationModal from "../ConfirmationModal";
import Menu from "@mui/material/Menu";
import { ReplyMessageContext } from "../../context/ReplyingMessage/ReplyingMessageContext";
import toastError from "../../errors/toastError";

const MessageOptionsMenu = ({
  message,
  menuOpen,
  handleClose,
  anchorEl,
  anchorPosition,
  onSelectMessage,
  onCopyMessage,
  onCopySelectedText,
  onReplyWithAI,
  onForwardMessage,
  onToggleHighlight,
  onReact,
  isHighlighted = false,
}) => {
  const { setReplyingMessage } = useContext(ReplyMessageContext);
  const [confirmationOpen, setConfirmationOpen] = useState(false);

  if (!message) {
    return null;
  }

  const handleSelectMessageAction = () => {
    if (onSelectMessage) {
      onSelectMessage(message);
    }
    handleClose();
  };

  const handleDeleteMessage = async () => {
    try {
      await api.delete(`/messages/${message.id}`);
    } catch (err) {
      toastError(err);
    }
  };

  const hanldeReplyMessage = () => {
    setReplyingMessage(message);
    handleClose();
  };

  const handleReplyWithAI = () => {
    if (onReplyWithAI) {
      onReplyWithAI(message);
    }
    handleClose();
  };

  const handleForwardMessage = () => {
    if (onForwardMessage) {
      onForwardMessage(message);
    }
    handleClose();
  };

  const handleCopyMessage = () => {
    if (onCopyMessage) {
      onCopyMessage(message);
    }
    handleClose();
  };

  const handleCopySelectedText = () => {
    if (onCopySelectedText) {
      onCopySelectedText(message);
    }
    handleClose();
  };

  const handleToggleHighlight = () => {
    if (onToggleHighlight) {
      onToggleHighlight(message);
    }
    handleClose();
  };

  const handleReact = (emoji) => {
    if (onReact) {
      onReact(message, emoji);
    }
    handleClose();
  };

  const handleOpenConfirmationModal = (e) => {
    setConfirmationOpen(true);
    handleClose();
  };

  return (
    <>
      <ConfirmationModal
        title={i18n.t("messageOptionsMenu.confirmationModal.title")}
        open={confirmationOpen}
        onClose={setConfirmationOpen}
        onConfirm={handleDeleteMessage}
      >
        {i18n.t("messageOptionsMenu.confirmationModal.message")}
      </ConfirmationModal>
      <Menu
        anchorEl={anchorEl}
        anchorReference={anchorPosition ? "anchorPosition" : "anchorEl"}
        anchorPosition={anchorPosition || undefined}
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "right",
        }}
        transformOrigin={{
          vertical: "top",
          horizontal: "right",
        }}
        open={menuOpen}
        onClose={handleClose}
      >
        {!message.fromMe && (
          <>
            <Box sx={{ px: 1.5, py: 1 }}>
              <Box sx={{ display: "flex", alignItems: "center", gap: 0.75, mb: 0.75 }}>
                <AddReactionIcon fontSize="small" color="action" />
                <Box component="span" sx={{ fontSize: 13, color: "text.secondary" }}>
                  Reaccionar
                </Box>
              </Box>
              <Box sx={{ display: "flex", gap: 0.5 }}>
                {["👍", "❤️", "😂", "😮", "😢", "🙏"].map((emoji) => (
                  <Box
                    key={emoji}
                    component="button"
                    type="button"
                    onClick={() => handleReact(emoji)}
                    sx={{
                      border: 0,
                      borderRadius: "50%",
                      bgcolor: "transparent",
                      cursor: "pointer",
                      fontSize: 20,
                      width: 32,
                      height: 32,
                      lineHeight: "32px",
                      p: 0,
                      "&:hover": {
                        bgcolor: "action.hover",
                      },
                    }}
                  >
                    {emoji}
                  </Box>
                ))}
                <Box
                  component="button"
                  type="button"
                  onClick={() => handleReact("")}
                  sx={{
                    border: 0,
                    borderRadius: "50%",
                    bgcolor: "transparent",
                    color: "text.secondary",
                    cursor: "pointer",
                    fontSize: 18,
                    width: 32,
                    height: 32,
                    lineHeight: "32px",
                    p: 0,
                    "&:hover": {
                      bgcolor: "action.hover",
                    },
                  }}
                >
                  ×
                </Box>
              </Box>
            </Box>
            <Divider />
          </>
        )}
        {message.fromMe ? (
          <MenuItem onClick={handleOpenConfirmationModal}>
            <ListItemIcon>
              <DeleteOutlineIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText>{i18n.t("messageOptionsMenu.delete")}</ListItemText>
          </MenuItem>
        ) : (
          <MenuItem disabled>
            <ListItemIcon>
              <DeleteOutlineIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText>{i18n.t("messageOptionsMenu.delete")}</ListItemText>
          </MenuItem>
        )}
        <MenuItem onClick={hanldeReplyMessage}>
          <ListItemIcon>
            <ReplyIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>Responder</ListItemText>
        </MenuItem>
        <MenuItem onClick={handleSelectMessageAction}>
          <ListItemIcon>
            <CheckBoxOutlinedIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>Seleccionar</ListItemText>
        </MenuItem>
        <MenuItem onClick={handleForwardMessage}>
          <ListItemIcon>
            <ForwardIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>Reenviar</ListItemText>
        </MenuItem>
        <MenuItem onClick={handleReplyWithAI}>
          <ListItemIcon>
            <AutoAwesomeIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>Responder con IA</ListItemText>
        </MenuItem>
        <MenuItem onClick={handleCopyMessage}>
          <ListItemIcon>
            <ContentCopyIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>Copiar mensaje</ListItemText>
        </MenuItem>
        <MenuItem onClick={handleCopySelectedText}>
          <ListItemIcon>
            <ContentCopyIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>Copiar selección</ListItemText>
        </MenuItem>
        <MenuItem onClick={handleToggleHighlight}>
          <ListItemIcon>
            {isHighlighted ? (
              <StarIcon fontSize="small" />
            ) : (
              <StarBorderIcon fontSize="small" />
            )}
          </ListItemIcon>
          <ListItemText>{isHighlighted ? "Quitar destacado" : "Destacar"}</ListItemText>
        </MenuItem>
      </Menu>
    </>
  );
};

export default MessageOptionsMenu;
